import { h } from '@omni-torn/ui';
import {
  estimateTrustWord,
  parseStatAmount,
  RECRUIT_PATH,
  transferFlows,
  transferTotals,
  TRANSFERS_PATH,
  WATCH_PATH,
  type FactionMoveRow,
  type FactionWatchAnswer,
  type RecruitAnswer,
  type RecruitQuery,
  type RecruitRow,
  type StackingAlert,
  type TransferDirection,
  type TransfersAnswer,
  type WatchedFactionView,
  type WatchHistoryAnswer,
} from '@omni-torn/shared';
import { server } from '../session';
import { ago, box, ext, kids, lineChart, loading, num, problem, profileUrl, short, table } from '../ui';
import { csvButton } from '../log-kit/ui';
import { ESTIMATE_NOTE, methodWord } from './estimate-view';
import { factionActivityBox } from './activity-view';

/**
 * Factions → Watch list, Recruitment, Transfers (website roadmap S24 — Torn Intel Pro's watch list, recruitment scanner
 * and transfers). Drawn from the Omni-Torn server (routes/faction-watch.ts): nothing here asks Torn from this browser.
 *  - /factions/watch: your faction's watch list — every member sees it, officers add and remove (up to 30); each watched
 *    faction's newest read (members, active, where they are, who's coming back), the "stacking before a war" line, and its
 *    patterns (active members over the last week, when its members play).
 *  - /factions/recruit: the player database searched by estimate band, level, activity, company, factionless.
 *  - /factions/transfers: who left which faction and where they went — a flow list and every move.
 */

export type ScoutPage = 'watch' | 'recruit' | 'transfers';

/** The Factions page's tabs (links): find a faction, the watch list, recruitment, transfers. */
export function factionsNav(current: ScoutPage | 'find'): HTMLElement {
  const items: Array<[ScoutPage | 'find', string, string]> = [
    ['find', 'Find & compare', '/factions'],
    ['watch', 'Watch list', '/factions/watch'],
    ['recruit', 'Recruitment', '/factions/recruit'],
    ['transfers', 'Transfers', '/factions/transfers'],
  ];
  return h('nav', { class: 'subnav', 'aria-label': 'Factions', 'data-factions-nav': '' }, ...items.map(([id, label, href]) => h('a', { href, 'data-link': true, class: id === current ? 'active' : '', ...(id === current ? { 'aria-current': 'page' } : {}) }, label)));
}

export async function renderScout(holder: HTMLElement, page: ScoutPage): Promise<void> {
  if (page === 'watch') return renderWatch(holder);
  if (page === 'recruit') return renderRecruit(holder);
  return renderTransfers(holder);
}

const factionLink = (id: number | null, names: Record<string, string> | Map<number, string>, none = 'No faction'): HTMLElement | string => {
  if (id === null) return h('span', { class: 'muted' }, 'not seen yet');
  if (id === 0) return h('span', { class: 'muted' }, none);
  const name = names instanceof Map ? names.get(id) : names[String(id)];
  return h('a', { href: `/factions/${id}`, 'data-link': true }, name ?? `Faction [${id}]`);
};

const estimateCell = (r: { total: number | null; totalSource: string | null; totalAt: number | null }): HTMLElement =>
  r.total ? h('span', { title: `${num(r.total)} — from ${methodWord(r.totalSource)}${r.totalAt ? `, ${ago(r.totalAt)}` : ''}` }, short(r.total)) : h('span', { class: 'muted' }, '·');

const playerCell = (id: number, name: string | null): HTMLElement => h('span', {}, h('a', { href: `/lookup/${id}`, 'data-link': true }, name ?? `[${id}]`), h('span', { class: 'muted small' }, ` [${id}] `), ext(profileUrl(id), '↗'));

// ---------------------------------------------------------------- the watch list

async function renderWatch(holder: HTMLElement): Promise<void> {
  document.title = 'Watch list · Factions · Omni-Torn';
  holder.replaceChildren(loading('Reading your faction’s watch list…'));
  let a: FactionWatchAnswer;
  try {
    a = await server<FactionWatchAnswer>('GET', WATCH_PATH);
  } catch (err) {
    holder.replaceChildren(box('Watch list', [problem(err), h('p', { class: 'muted small' }, 'The watch list belongs to your faction: you need to be in one (and signed in with Omni-Torn).')], { id: 'watch-list' }));
    return;
  }
  drawWatch(holder, a);
}

const levelWord: Record<StackingAlert['level'], string> = { stacking: 'Stacking', watch: 'Watch', quiet: 'Quiet' };

function alertCell(v: WatchedFactionView): HTMLElement {
  if (!v.now) return h('span', { class: 'muted' }, v.problem ?? 'not read yet');
  const al = v.alert;
  if (!al || al.level === 'quiet') return h('span', { class: 'muted' }, al?.reasons.length ? al.reasons.join(' · ') : 'quiet');
  return h('span', { class: `watch-alert watch-${al.level}`, 'data-alert': al.level }, h('strong', {}, `${levelWord[al.level]}: `), al.reasons.join(' · '));
}

function drawWatch(holder: HTMLElement, a: FactionWatchAnswer): void {
  const status = h('p', { class: 'form-status', role: 'status', 'data-watch': 'status' });
  const patterns = h('div', { 'data-watch': 'patterns' });
  const act = async (fn: () => Promise<FactionWatchAnswer>, done: string) => {
    status.className = 'form-status';
    status.textContent = 'Saving…';
    try {
      const next = await fn();
      drawWatch(holder, next);
      const s = holder.querySelector('[data-watch=status]');
      if (s) (s.className = 'form-status ok'), (s.textContent = done);
    } catch (err) {
      status.className = 'form-status bad';
      status.textContent = (err as Error).message;
    }
  };
  // Adding: officers only, after agreeing to the terms that name the watch list.
  let add: HTMLElement | null = null;
  if (a.officer) {
    const input = h('input', { type: 'search', placeholder: 'Faction id or Torn link', 'aria-label': 'Faction to watch', maxlength: '120', 'data-watch': 'add-input' }) as HTMLInputElement;
    const form = h('form', { class: 'compare-form', 'data-form': 'watch-add' }, input, h('button', { type: 'submit', ...(a.agreed ? {} : { disabled: true }) }, 'Watch'));
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const m = /^\[?(\d{1,10})\]?$/.exec(input.value.trim())?.[1] ?? /factions\.php\?[^#]*\bID=(\d{1,10})/i.exec(input.value)?.[1];
      if (!m) {
        status.className = 'form-status bad';
        status.textContent = 'Type the faction’s id or paste its Torn link.';
        return;
      }
      void act(() => server<FactionWatchAnswer>('POST', WATCH_PATH, { factionId: Number(m) }), 'Added — Omni-Torn reads it every few minutes from now on.');
    });
    add = h('div', {}, form, a.agreed ? null : h('p', { class: 'notice', 'data-watch': 'terms' }, `Adding factions needs Omni-Torn’s terms version ${a.terms} — the terms table says what the watch list reads and shows. Once it’s out, continuing to use Omni-Torn after the “terms changed” banner agrees to it.`));
  }
  const alerts = a.factions.filter((v) => v.alert && v.alert.level !== 'quiet').sort((x, y) => (x.alert!.level === 'stacking' ? 0 : 1) - (y.alert!.level === 'stacking' ? 0 : 1));
  const showPatterns = async (v: WatchedFactionView) => {
    patterns.replaceChildren(loading(`Reading ${v.name ?? `faction ${v.id}`}’s last week…`));
    try {
      const hist = await server<WatchHistoryAnswer>('GET', `${WATCH_PATH}/${v.id}?days=7`);
      const pts = hist.reads.map((r) => ({ at: r.at, v: r.active }));
      const away = hist.reads.map((r) => ({ at: r.at, v: r.flying + r.abroad }));
      const activity = await factionActivityBox(v.id, `When ${v.name ?? 'they'} play`);
      patterns.replaceChildren(
        box(
          `${v.name ?? `Faction ${v.id}`} — the last week`,
          [
            h('h3', {}, 'Active in the last 15 minutes, read by read'),
            lineChart(pts, { label: 'active members', format: (n) => num(n), xFormat: (ms) => new Date(ms).toISOString().slice(5, 16).replace('T', ' ') }),
            h('h3', {}, 'Away (flying or abroad)'),
            lineChart(away, { label: 'away', format: (n) => num(n), xFormat: (ms) => new Date(ms).toISOString().slice(5, 16).replace('T', ' ') }),
            h('p', { class: 'muted small' }, `${hist.reads.length} reads kept. Times in TCT.`),
          ],
          { id: 'watch-patterns' },
        ),
        activity,
      );
    } catch (err) {
      patterns.replaceChildren(problem(err));
    }
  };
  const rows = a.factions;
  holder.replaceChildren(
    ...kids(
      alerts.length
        ? box(
            'Stacking before a war?',
            h('ul', { class: 'plain-list' }, ...alerts.map((v) => h('li', { 'data-watch-alert': String(v.id) }, h('a', { href: `/factions/${v.id}`, 'data-link': true }, v.name ?? `Faction [${v.id}]`), ' — ', alertCell(v)))),
            { id: 'watch-alerts' },
          )
        : null,
      box(
        `Watch list (${rows.length} of ${a.max})`,
        [
          add,
          status,
          table(
            rows,
            [
              { label: 'Faction', get: (v) => h('span', {}, h('a', { href: `/factions/${v.id}`, 'data-link': true }, v.name ?? `Faction [${v.id}]`), v.tag ? h('span', { class: 'muted small' }, ` [${v.tag}]`) : null), sort: (v) => (v.name ?? '').toLowerCase() },
              { label: 'Members', get: (v) => num(v.now?.members), sort: (v) => v.now?.members ?? -1, cls: 'r' },
              { label: 'Active 15m', get: (v) => num(v.now?.active), sort: (v) => v.now?.active ?? -1, cls: 'r' },
              { label: 'Online', get: (v) => num(v.now?.online), sort: (v) => v.now?.online ?? -1, cls: 'r' },
              { label: 'Okay', get: (v) => num(v.now?.okay), sort: (v) => v.now?.okay ?? -1, cls: 'r' },
              { label: 'Hosp · jail', get: (v) => (v.now ? `${num(v.now.hospital)} · ${num(v.now.jail)}` : '—'), sort: (v) => (v.now ? v.now.hospital + v.now.jail : -1), cls: 'r' },
              { label: 'Away', get: (v) => (v.now ? num(v.now.flying + v.now.abroad) : '—'), sort: (v) => (v.now ? v.now.flying + v.now.abroad : -1), cls: 'r' },
              { label: 'Back <1h', get: (v) => num(v.now?.returning), sort: (v) => v.now?.returning ?? -1, cls: 'r' },
              { label: 'Read', get: (v) => (v.now ? ago(v.now.at) : '—'), sort: (v) => v.now?.at ?? 0 },
              { label: 'Alert', get: (v) => alertCell(v), sort: (v) => (v.alert?.level === 'stacking' ? 2 : v.alert?.level === 'watch' ? 1 : 0) },
              {
                label: '',
                get: (v) =>
                  h(
                    'span',
                    { class: 'row-actions' },
                    h('button', { type: 'button', class: 'btn-small', 'data-watch-patterns': String(v.id), onclick: () => void showPatterns(v) }, 'Patterns'),
                    ' ',
                    h('a', { href: `/factions/transfers?faction=${v.id}`, 'data-link': true, class: 'small' }, 'Transfers'),
                    a.officer ? h('button', { type: 'button', class: 'btn-small', 'data-watch-remove': String(v.id), onclick: () => void act(() => server<FactionWatchAnswer>('DELETE', `${WATCH_PATH}/${v.id}`), 'Taken off the list.') }, 'Remove') : null,
                  ),
              },
            ],
            { initial: 9, empty: a.officer ? 'Nothing watched yet — add a faction above.' : 'Nothing watched yet — your faction’s leader, co-leader or a member they allowed (Faction → Delegates) can add factions.' },
          ),
          h(
            'p',
            { class: 'muted small', 'data-watch': 'note' },
            `Every member of your faction sees this list; officers (leader, co-leader, members they allowed on Faction → Delegates) add and remove — up to ${a.max}. `,
            `Omni-Torn’s server reads each faction’s public member list about every ${a.everyMinutes} minutes (keys players lend first, else its own key; a few a minute for every list together, so with many lists it can take longer), while someone of your faction looks at the list at least once a week. `,
            '“Stacking” = members coming back from travel or hospital within the hour, more of them active than usual at this time of day, fewer away than a while ago — and a ranked war about to start. ',
            a.readable ? '' : 'This server has no key to read Torn with just now: the list waits.',
          ),
        ],
        { id: 'watch-list' },
      ),
      patterns,
    ),
  );
}

// ---------------------------------------------------------------- recruitment

const SEEN_DAYS: Array<[string, string]> = [['1', 'today'], ['3', '3 days'], ['7', 'a week'], ['14', '2 weeks'], ['30', 'a month']];
const HOURS: Array<[string, string]> = [['0', 'any'], ['1', '1 h'], ['2', '2 h'], ['4', '4 h'], ['6', '6 h'], ['8', '8 h']];

function renderRecruit(holder: HTMLElement): void {
  document.title = 'Recruitment · Factions · Omni-Torn';
  const input = (name: string, placeholder: string, type = 'text') => h('input', { type, name, placeholder, 'aria-label': placeholder, ...(type === 'number' ? { min: '1', max: '100' } : { maxlength: '20' }) }) as HTMLInputElement;
  const sel = (name: string, label: string, opts: Array<[string, string]>, value: string) => h('select', { name, 'aria-label': label }, ...opts.map(([v, t]) => h('option', { value: v, ...(v === value ? { selected: true } : {}) }, t))) as HTMLSelectElement;
  const totalMin = input('totalMin', 'Estimate from (e.g. 50m)');
  const totalMax = input('totalMax', 'Estimate to (e.g. 2b)');
  const unknown = h('input', { type: 'checkbox', name: 'includeUnknown' }) as HTMLInputElement;
  const levelMin = input('levelMin', 'Level from', 'number');
  const levelMax = input('levelMax', 'Level to', 'number');
  const faction = sel('faction', 'Faction', [['none', 'Not in a faction'], ['any', 'Any'], ['some', 'In a faction']], 'none');
  const company = sel('company', 'Company', [['any', 'Any company'], ['some', 'In a company'], ['none', 'Not in a company']], 'any');
  const seen = sel('seenDays', 'Last action within', SEEN_DAYS, '7');
  const hours = sel('hoursMin', 'Active hours a day at least', HOURS, '0');
  const sort = sel('sort', 'Sort', [['active', 'Last active first'], ['estimate', 'Strongest first'], ['level', 'Highest level first']], 'active');
  const status = h('p', { class: 'form-status', role: 'status' });
  const results = h('div', {});
  const form = h(
    'form',
    { 'data-form': 'recruit' },
    h('div', { class: 'field' }, h('label', {}, 'Estimate '), totalMin, ' to ', totalMax, h('label', { class: 'small' }, unknown, ' also players with no estimate yet')),
    h('div', { class: 'field' }, h('label', {}, 'Level '), levelMin, ' to ', levelMax),
    h('div', { class: 'field' }, faction, company, h('label', {}, 'last action within '), seen, h('label', {}, 'active at least '), hours, h('span', { class: 'muted small' }, 'a day')),
    h('div', { class: 'field' }, sort, h('button', { type: 'submit', class: 'btn-primary' }, 'Search')),
    status,
  );
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const amount = (i: HTMLInputElement) => (i.value.trim() ? parseStatAmount(i.value) : null);
    const lo = amount(totalMin);
    const hi = amount(totalMax);
    if ((totalMin.value.trim() && lo === null) || (totalMax.value.trim() && hi === null)) {
      status.className = 'form-status bad';
      status.textContent = 'Estimates like 50m, 1.5b or 2,000,000.';
      return;
    }
    const q: RecruitQuery = {
      totalMin: lo,
      totalMax: hi,
      includeUnknown: unknown.checked,
      levelMin: levelMin.value ? Number(levelMin.value) : null,
      levelMax: levelMax.value ? Number(levelMax.value) : null,
      faction: faction.value as RecruitQuery['faction'],
      company: company.value as RecruitQuery['company'],
      seenDays: Number(seen.value),
      hoursMin: Number(hours.value) || null,
      sort: sort.value as RecruitQuery['sort'],
      limit: 100,
    };
    status.className = 'form-status';
    status.textContent = 'Searching…';
    try {
      const a = await server<RecruitAnswer>('POST', RECRUIT_PATH, q);
      status.textContent = '';
      results.replaceChildren(recruitResults(a));
    } catch (err) {
      status.className = 'form-status bad';
      status.textContent = (err as Error).message;
    }
  });
  holder.replaceChildren(
    box(
      'Recruitment',
      [
        h('p', { class: 'muted' }, 'Players Omni-Torn knows, by estimate, level, how much they play and whether they’re in a faction or a company. A faction’s recruiter can send them a message from their Torn profile.'),
        form,
      ],
      { id: 'recruit-form' },
    ),
    results,
  );
}

function recruitResults(a: RecruitAnswer): HTMLElement {
  const rows = a.rows;
  const company = (r: RecruitRow) => (r.company ? (r.company.id === 0 ? h('span', { class: 'muted' }, 'none') : h('span', { title: r.company.type !== null ? `company type ${r.company.type}` : '' }, r.company.name ?? `[${r.company.id}]`)) : h('span', { class: 'muted' }, '—'));
  return box(
    `${num(rows.length)} player${rows.length === 1 ? '' : 's'}${a.more ? ' (more match — narrow it down)' : ''}`,
    [
      h(
        'div',
        { class: 'toolbar' },
        csvButton(
          () => 'recruitment.csv',
          () => ({
            header: ['id', 'name', 'level', 'faction id', 'estimate', 'estimate from', 'last action (UTC)', 'active hours a day (30 days)', 'days active', 'company'],
            rows: rows.map((r) => [r.id, r.name, r.level, r.factionId, r.total, r.totalSource, r.lastAction ? new Date(r.lastAction).toISOString() : '', r.hoursPerDay, r.activeDays, r.company?.name ?? '']),
          }),
        ),
      ),
      table(
        rows,
        [
          { label: 'Player', get: (r) => playerCell(r.id, r.name), sort: (r) => (r.name ?? '').toLowerCase() },
          { label: 'Level', get: (r) => num(r.level), sort: (r) => r.level ?? 0, cls: 'r' },
          { label: 'Estimate', get: (r) => estimateCell(r), sort: (r) => r.total ?? -1, cls: 'r' },
          { label: 'Trust', get: (r) => (r.total ? estimateTrustWord(r.totalSource, r.totalAt ? Date.now() - r.totalAt : 0) : '—') },
          { label: 'Faction', get: (r) => factionLink(r.factionId ?? 0, {}, 'none'), sort: (r) => r.factionId ?? 0 },
          { label: 'Company', get: (r) => company(r) },
          { label: 'Plays', get: (r) => (r.hoursPerDay === null ? h('span', { class: 'muted' }, 'not seen enough') : `${r.hoursPerDay < 0.1 && r.activeDays ? '<0.1' : r.hoursPerDay.toFixed(1)} h a day · ${r.activeDays} day${r.activeDays === 1 ? '' : 's'}`), sort: (r) => r.hoursPerDay ?? -1 },
          { label: 'Last action', get: (r) => (r.lastAction ? ago(r.lastAction) : '—'), sort: (r) => r.lastAction ?? 0 },
        ],
        { initial: 7, empty: 'Nobody Omni-Torn knows matches — widen the band or the days.', csv: false },
      ),
      h(
        'p',
        { class: 'muted small' },
        'Estimates are Omni-Torn’s own (', ESTIMATE_NOTE.replace(/\.$/, ''), '). ',
        `“Plays”: Omni-Torn’s activity tracker over the last 30 days. Company: only where Omni-Torn users on terms ${a.terms} saw it. ${num(a.weighed)} weighed.`,
      ),
    ],
    { id: 'recruit-results' },
  );
}

// ---------------------------------------------------------------- transfers

function renderTransfers(holder: HTMLElement): void {
  document.title = 'Transfers · Factions · Omni-Torn';
  const params = new URLSearchParams(location.search);
  const input = h('input', { type: 'search', placeholder: 'Faction id or Torn link (empty: every faction)', 'aria-label': 'Faction', maxlength: '120', value: params.get('faction') ?? '' }) as HTMLInputElement;
  const dir = h('select', { 'aria-label': 'Which way' }, ...([['both', 'Left and joined'], ['left', 'Left'], ['joined', 'Joined']] as const).map(([v, t]) => h('option', { value: v, ...(v === (params.get('dir') ?? 'both') ? { selected: true } : {}) }, t))) as HTMLSelectElement;
  const days = h('select', { 'aria-label': 'Days' }, ...([['7', 'last 7 days'], ['30', 'last 30 days'], ['90', 'last 90 days'], ['365', 'last year']] as const).map(([v, t]) => h('option', { value: v, ...(v === (params.get('days') ?? '30') ? { selected: true } : {}) }, t))) as HTMLSelectElement;
  const status = h('p', { class: 'form-status', role: 'status' });
  const results = h('div', {});
  const form = h('form', { class: 'compare-form', 'data-form': 'transfers' }, input, dir, days, h('button', { type: 'submit' }, 'Show'), status);
  let asked = 0;
  const load = async () => {
    const mine = ++asked;
    const raw = input.value.trim();
    const id = raw ? (/^\[?(\d{1,10})\]?$/.exec(raw)?.[1] ?? /factions\.php\?[^#]*\bID=(\d{1,10})/i.exec(raw)?.[1]) : '';
    if (raw && !id) {
      status.className = 'form-status bad';
      status.textContent = 'Type the faction’s id or paste its Torn link.';
      return;
    }
    const q = new URLSearchParams({ dir: dir.value, days: days.value, ...(id ? { faction: id } : {}) });
    history.replaceState(null, '', `/factions/transfers?${q}`);
    status.className = 'form-status';
    status.textContent = 'Reading…';
    try {
      const a = await server<TransfersAnswer>('GET', `${TRANSFERS_PATH}?${q}`);
      // A later ask (the form sent again) wins.
      if (mine !== asked) return;
      status.textContent = '';
      results.replaceChildren(...transferResults(a));
    } catch (err) {
      if (mine !== asked) return;
      status.className = 'form-status bad';
      status.textContent = (err as Error).message;
    }
  };
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    void load();
  });
  holder.replaceChildren(box('Transfers', [h('p', { class: 'muted' }, 'Who left which faction, and where they went — as Omni-Torn users and its watch lists saw it.'), form], { id: 'transfers-form' }), results);
  void load();
}

const dirWord: Record<TransferDirection, string> = { both: 'left or joined', left: 'left', joined: 'joined' };

function transferResults(a: TransfersAnswer): HTMLElement[] {
  const names = a.factions;
  const flows = transferFlows(a.moves);
  const top = Math.max(1, ...flows.map((f) => f.n));
  const totals = a.factionId !== null ? transferTotals(a.moves, a.factionId) : null;
  const flowBox = box(
    'Where they went',
    flows.length
      ? h(
          'ul',
          { class: 'bars-list flows', 'data-transfers': 'flows' },
          ...flows.map((f) => {
            const fill = h('span', { class: 'bl-fill' });
            fill.style.width = `${(f.n / top) * 100}%`;
            return h('li', {}, h('span', { class: 'bl-label flow-label' }, factionLink(f.from, names, 'no faction'), ' → ', factionLink(f.to, names, 'no faction')), h('span', { class: 'bl-track' }, fill), h('span', { class: 'bl-n' }, num(f.n)));
          }),
        )
      : h('p', { class: 'muted' }, 'No moves seen yet.'),
    { id: 'transfer-flows' },
  );
  const tiles = totals
    ? h(
        'div',
        { class: 'tiles', 'data-transfers': 'totals' },
        h('div', { class: 'tile' }, h('div', { class: 'tile-label' }, 'Left'), h('div', { class: 'tile-value' }, num(totals.left))),
        h('div', { class: 'tile' }, h('div', { class: 'tile-label' }, 'Joined'), h('div', { class: 'tile-value' }, num(totals.joined))),
        h('div', { class: 'tile' }, h('div', { class: 'tile-label' }, 'Net'), h('div', { class: `tile-value ${totals.net < 0 ? 't-red' : totals.net > 0 ? 't-green' : ''}`.trim() }, totals.net > 0 ? `+${totals.net}` : String(totals.net))),
      )
    : null;
  const list = box(
    `${num(a.moves.length)} move${a.moves.length === 1 ? '' : 's'}${a.more ? ' (the newest)' : ''} — ${a.factionId !== null ? `${names[String(a.factionId)] ?? `faction ${a.factionId}`}, ${dirWord[a.direction]}` : 'every faction'}, last ${a.days} days`,
    [
      ...kids(tiles),
      h(
        'div',
        { class: 'toolbar' },
        csvButton(
          () => `transfers${a.factionId ? `-${a.factionId}` : ''}.csv`,
          () => ({
            header: ['player id', 'name', 'level', 'from faction', 'to faction', 'seen moved (UTC)', 'last seen in the old one (UTC)', 'estimate'],
            rows: a.moves.map((m) => [m.playerId, m.name, m.level, m.from, m.to, new Date(m.at).toISOString(), m.leftAfter ? new Date(m.leftAfter).toISOString() : '', m.total]),
          }),
        ),
      ),
      table(
        a.moves,
        [
          { label: 'Player', get: (m: FactionMoveRow) => playerCell(m.playerId, m.name), sort: (m) => (m.name ?? '').toLowerCase() },
          { label: 'Level', get: (m) => num(m.level), sort: (m) => m.level ?? 0, cls: 'r' },
          { label: 'Estimate', get: (m) => estimateCell(m), sort: (m) => m.total ?? -1, cls: 'r' },
          { label: 'From', get: (m) => (m.from === null ? h('span', { class: 'muted' }, 'not known') : factionLink(m.from, names)) },
          { label: 'To', get: (m) => factionLink(m.to, names) },
          { label: 'When', get: (m) => h('span', { title: m.leftAfter ? `last seen in the old faction ${ago(m.leftAfter)}` : '' }, ago(m.at)), sort: (m) => m.at },
        ],
        { initial: 5, empty: 'No moves seen in these days.', csv: false },
      ),
      h('p', { class: 'muted small' }, `From what Omni-Torn users saw (those on terms ${a.terms}) and the watch lists’ reads — not every move in Torn. “not seen yet”: gone from the faction, not seen in another one so far.`),
    ],
    { id: 'transfer-moves' },
  );
  return [flowBox, list];
}
